/**
 * When each service on a proposal runs, worked out from the services sold.
 *
 * The price list gives each service a length ("2–3 weeks"); the order and the
 * overlaps are the studio's way of working, as the Figma timeline draws it:
 * positioning first, architecture overlapping its last week, the identity once
 * positioning is approved, the voice in the identity's last week, templates
 * last; the website after the identity and the CRM built during the second
 * half of the website. A service the rules do not know follows whatever came
 * before it.
 *
 * Pure, and free of imports from the renderers, so the printed document, the
 * public page and the generator all draw the same weeks.
 */

const text = (v) => String(v ?? '').trim()

/**
 * Where a known service starts. `after` lists the services it follows (the
 * latest one present wins); `overlap` is how many weeks it shares with that
 * one, or 'half' to start halfway through it; `fallback` is used when none of
 * `after` is on the proposal.
 */
export const SEQUENCE = [
  ['positioning-messaging', { after: [] }],
  ['brand-architecture', { after: ['positioning-messaging'], overlap: 1 }],
  ['visual-identity-system', { after: ['positioning-messaging'], overlap: 0, fallback: ['brand-architecture'] }],
  ['brand-voice-guidelines', { after: ['visual-identity-system'], overlap: 1, fallback: ['positioning-messaging', 'brand-architecture'] }],
  ['website-design', { after: ['visual-identity-system', 'positioning-messaging'], overlap: 0 }],
  ['crm-marketing-automation', { after: ['website-design'], overlap: 'half', fallback: ['visual-identity-system', 'positioning-messaging'] }],
  ['funnels-campaigns', { after: ['crm-marketing-automation'], overlap: 0, fallback: ['website-design', 'visual-identity-system', 'positioning-messaging'] }],
  ['templates-rollout', { after: ['brand-voice-guidelines', 'visual-identity-system', 'brand-architecture', 'positioning-messaging'], overlap: 0 }],
]
const RULES = Object.fromEntries(SEQUENCE)
const ORDER = SEQUENCE.map(([id]) => id)

/**
 * A schedule string as a length or a fixed position.
 * "2–3 weeks" → { weeks: 3 }; "Weeks 4–9" → { start: 4, end: 9 };
 * "Monthly…" → null. Falls back to the service's weeks_max / weeks_min.
 */
export function readSchedule(schedule, service) {
  const s = text(schedule)
  if (/month/i.test(s)) return null
  const fixed = s.match(/^weeks?\s+(\d+)(?:\s*[–-]\s*(\d+))?/i)
  if (fixed) {
    const a = Number(fixed[1])
    const b = Number(fixed[2] || fixed[1])
    return a > 0 && b >= a ? { start: a, end: b, weeks: b - a + 1 } : null
  }
  const range = s.match(/(\d+)\s*[–-]\s*(\d+)/)
  if (range) return Number(range[2]) ? { weeks: Number(range[2]) } : null
  const one = s.match(/(\d+)\s*(week|wk)/i) || s.match(/^(\d+)$/)
  if (one) return Number(one[1]) ? { weeks: Number(one[1]) } : null
  const hi = Number(service?.weeks_max) || Number(service?.weeks_min)
  return hi ? { weeks: hi } : null
}

const isRetainer = (it) => it?.kind === 'retainer' || /month/i.test(text(it?.schedule))

/**
 * The bars: one per scope item with a length, in the scope's order.
 * `items` are scope items ({ title, schedule, service_id, kind }); `byId`
 * maps service ids to price-list rows for a length the item does not state.
 */
export function scheduleServices(items = [], byId = {}) {
  const list = (Array.isArray(items) ? items : [])
    .map((it, index) => ({ it, index }))
    .filter(({ it }) => (text(it?.title) || text(it?.description)) && !isRetainer(it))
    .map(({ it, index }) => ({ index, it, read: readSchedule(it.schedule, it.service_id ? byId[it.service_id] : null) }))
    .filter(x => x.read)

  const placed = {}
  const out = new Map()
  const endOf = (ids) => ids.map(id => placed[id]).filter(Boolean)

  // Fixed positions first: a schedule that names its weeks keeps them.
  for (const x of list) {
    if (x.read.start) {
      out.set(x.index, { start: x.read.start, end: x.read.end })
      if (x.it.service_id && !placed[x.it.service_id]) placed[x.it.service_id] = out.get(x.index)
    }
  }

  // Known services, in the studio's order, once each.
  for (const id of ORDER) {
    const x = list.find(l => l.it.service_id === id && !out.has(l.index))
    if (!x) continue
    const rule = RULES[id]
    let prev = endOf(rule.after)
    let overlap = rule.overlap || 0
    if (!prev.length && rule.fallback) { prev = endOf(rule.fallback); overlap = 0 }
    let start = 1
    if (prev.length) {
      const last = prev.reduce((a, b) => (b.end > a.end ? b : a))
      start = overlap === 'half'
        ? last.start + Math.ceil((last.end - last.start + 1) / 2)
        : Math.max(1, last.end - overlap + 1)
    }
    const bar = { start, end: start + x.read.weeks - 1 }
    out.set(x.index, bar)
    placed[id] = bar
  }

  // Anything else follows what came before it.
  for (const x of list) {
    if (out.has(x.index)) continue
    const end = Math.max(0, ...[...out.values()].map(b => b.end))
    out.set(x.index, { start: end + 1, end: end + x.read.weeks })
  }

  const bars = list.map(x => ({
    index: x.index,
    service_id: x.it.service_id || null,
    title: text(x.it.title),
    start: out.get(x.index).start,
    end: out.get(x.index).end,
    weeks: out.get(x.index).end - out.get(x.index).start + 1,
  }))
  const totalWeeks = bars.length ? Math.max(...bars.map(b => b.end)) : 0
  return { bars, totalWeeks }
}

const STRATEGY = ['positioning-messaging', 'brand-architecture']

/**
 * The five stages (discovery, strategy, design, revisions, delivery) as week
 * ranges over the same schedule. Strategy ends when the strategy services do;
 * delivery is the templates' weeks, or the last week; revisions are the two
 * weeks before delivery; design is what lies between.
 */
export function stageWeeks(bars = [], totalWeeks = 0) {
  const T = totalWeeks
  if (!T) return [null, null, null, null, null]
  if (T < 6) {
    // Too short to divide by service; shared out by the usual proportions.
    const shares = [0, 0.23, 0.31, 0.69, 0.85, 1]
    return shares.slice(0, 5).map((s, i) => {
      const a = Math.min(T, Math.floor(s * T) + 1)
      const b = Math.max(a, Math.round(shares[i + 1] * T))
      return [a, Math.min(T, b)]
    })
  }
  const strat = bars.filter(b => STRATEGY.includes(b.service_id))
  const strategyEnd = strat.length ? Math.max(...strat.map(b => b.end)) : Math.max(2, Math.round(T * 0.2))
  const discEnd = Math.max(1, Math.floor(strategyEnd / 3))
  const tpl = bars.find(b => b.service_id === 'templates-rollout')
  const deliveryStart = tpl ? tpl.start : T
  const revEnd = Math.max(strategyEnd + 1, deliveryStart - 1)
  const revStart = Math.max(strategyEnd + 2, revEnd - 1)
  const clamp = ([a, b]) => {
    const x = Math.min(T, Math.max(1, a))
    return [x, Math.min(T, Math.max(x, b))]
  }
  return [
    clamp([1, discEnd]),
    clamp([Math.min(discEnd + 1, strategyEnd), strategyEnd]),
    clamp([strategyEnd + 1, Math.max(strategyEnd + 1, revStart - 1)]),
    clamp([revStart, revEnd]),
    clamp([deliveryStart, T]),
  ]
}

/** "Week 3", "Weeks 4–9". */
export const weeksLabel = (a, b) => (a && b ? (a === b ? `Week ${a}` : `Weeks ${a}–${b}`) : '')

/** Up to four milestones: kickoff, then the services that finish last. */
export function milestonesFrom(bars = []) {
  const done = [...bars].sort((a, b) => a.end - b.end || a.index - b.index)
  const picks = done.length > 3 ? [done[0], done[Math.floor(done.length / 2)], done[done.length - 1]] : done
  return [
    { week: 1, label: 'Kickoff workshop' },
    ...picks.map(b => ({ week: b.end, label: `${b.title} delivered` })),
  ].slice(0, 4)
}
